import type { BlockField } from '@/blocks/domain/types';
import type { BlockFieldDto } from '@/blocks/application/mappers/blockMapper';

/**
 * Keeps the first field of each id. A block stored with a repeated field id (data written before
 * LLAMS-686) would otherwise reopen with the element twice in the Sections panel and on the canvas.
 */
function withoutRepeatedIds(dtos: BlockFieldDto[]): BlockFieldDto[] {
  const seen = new Set<string>();
  return dtos.filter((dto) => {
    if (seen.has(dto.id)) return false;
    seen.add(dto.id);
    return true;
  });
}

/**
 * The backend stores block content JSON verbatim (shape + a 5 MB limit only), so the 12-value
 * `BlockFieldStyle` contract (LLAMS-610 Part 7) passes through unchanged both ways — no coercion
 * to the 3 legacy values. Unknown-shaped imported JSON is still guarded by `normalizeFieldRecord.ts`
 * upstream of this mapper.
 */
export function mapFieldsDto(dtos: BlockFieldDto[]): BlockField[] {
  return withoutRepeatedIds(dtos).map((f, idx) => {
    const field: BlockField = {
      id: f.id,
      label: f.label,
      value: f.value,
      width: f.width,
      align: f.align,
      style: f.style,
      visible: f.visible,
      sortOrder: typeof f.sort_order === 'number' ? f.sort_order : idx
    };
    if (f.label_visible !== undefined) field.labelVisible = f.label_visible;
    if (f.value_visible !== undefined) field.valueVisible = f.value_visible;
    if (f.name !== undefined) field.name = f.name;
    if (f.orientation !== undefined) field.orientation = f.orientation;
    if (f.column !== undefined) field.column = f.column;
    if (f.section_id !== undefined) field.sectionId = f.section_id;
    if (f.media !== undefined) field.media = f.media;
    if (f.italic !== undefined) field.italic = f.italic;
    if (f.uppercase !== undefined) field.uppercase = f.uppercase;
    return field;
  });
}

export function fieldsToDto(fields: BlockField[]): BlockFieldDto[] {
  return fields.map((f, idx) => {
    const dto: BlockFieldDto = {
      id: f.id,
      label: f.label,
      value: f.value,
      width: f.width,
      align: f.align,
      style: f.style,
      visible: f.visible,
      sort_order: typeof f.sortOrder === 'number' ? f.sortOrder : idx
    };
    if (f.labelVisible !== undefined) dto.label_visible = f.labelVisible;
    if (f.valueVisible !== undefined) dto.value_visible = f.valueVisible;
    if (f.name !== undefined) dto.name = f.name;
    if (f.orientation !== undefined) dto.orientation = f.orientation;
    if (f.column !== undefined) dto.column = f.column;
    if (f.sectionId !== undefined) dto.section_id = f.sectionId;
    if (f.media !== undefined) dto.media = f.media;
    if (f.italic !== undefined) dto.italic = f.italic;
    if (f.uppercase !== undefined) dto.uppercase = f.uppercase;
    return dto;
  });
}
